<template>
    <BModal
        :model-value="modal"
        centered
        @update:modelValue="updateModal"
        scrollable
        no-close-on-backdrop
    >
        <template #header>
            <div
                class="d-flex align-items-center justify-content-between modal-title w-100"
            >
                <h5>Currency</h5>
                <BButton
                    variant="link"
                    class="close"
                    @click="updateModal(false)"
                >
                    <i class="ion ion-android-close"></i>
                </BButton>
            </div>
        </template>

        <BForm @submit.stop.prevent>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Code *"
                        label-for="code"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.code"
                            type="text"
                            id="code"
                            placeholder="Enter Code (e.g. IDR)"
                            :class="{
                                'is-invalid':
                                    v$.form.code.$dirty && v$.form.code.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="v$.form.code.$dirty && v$.form.code.$invalid"
                        >
                            {{ v$.form.code.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Name *"
                        label-for="name"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.name"
                            type="text"
                            id="name"
                            placeholder="Enter Name (e.g. Indonesian Rupiah)"
                            :class="{
                                'is-invalid':
                                    v$.form.name.$dirty && v$.form.name.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="v$.form.name.$dirty && v$.form.name.$invalid"
                        >
                            {{ v$.form.name.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Symbol *"
                        label-for="symbol"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.symbol"
                            type="text"
                            id="symbol"
                            placeholder="Enter Symbol (e.g. Rp)"
                            :class="{
                                'is-invalid':
                                    v$.form.symbol.$dirty && v$.form.symbol.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="v$.form.symbol.$dirty && v$.form.symbol.$invalid"
                        >
                            {{ v$.form.symbol.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
        </BForm>

        <template #footer>
            <div class="d-flex justify-content-end">
                <BButton variant="primary" class="me-2" @click="handleAction"
                    >Save</BButton
                >
                <BButton variant="danger" @click="updateModal(false)"
                    >Cancel</BButton
                >
            </div>
        </template>
    </BModal>
</template>

<script setup>
import axios from "axios";
import { defineProps, defineEmits, reactive, watch } from "vue";
import {
    BModal,
    BRow,
    BCol,
    BForm,
    BFormInput,
    BButton,
    BFormGroup,
    BFormInvalidFeedback,
} from "bootstrap-vue-next";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, maxLength } from "@vuelidate/validators";
import { useLoading } from "vue-loading-overlay";
import { useAuthStore } from "@/store/auth.js";
import { useToast } from "vue-toastification";

const auth = useAuthStore();
const $loading = useLoading();
const toast = useToast();

const props = defineProps({
    modal: Boolean,
    selectedData: Object,
});

const emit = defineEmits(["update:modal", "addOrUpdate"]);

const updateModal = (value) => {
    emit("update:modal", value);
};

const resetForm = () => {
    Object.assign(form, { ...formData });
    v$.value.$reset();
};

const formData = {
    code: "",
    name: "",
    symbol: "",
};

const form = reactive({ ...formData });

watch(
    () => props.selectedData,
    (newData) => {
        if (newData && Object.keys(newData).length > 0) {
            Object.assign(form, { ...newData });
        } else {
            Object.assign(form, { ...formData });
        }
    },
    { immediate: true }
);

const rules = {
    form: {
        code: {
            required: helpers.withMessage("Code is required", required),
            maxLength: helpers.withMessage("Max 3 characters", maxLength(3)),
        },
        name: {
            required: helpers.withMessage("Name is required", required),
        },
        symbol: {
            required: helpers.withMessage("Symbol is required", required),
        },
    },
};

const v$ = useVuelidate(rules, { form });

const handleAction = () => {
    props.selectedData.uuid ? update() : create();
};

const create = async () => {
    v$.value.$touch();
    if (!v$.value.$invalid) {
        const loader = $loading.show({
            color: "#489464",
            loader: "spinner",
        });

        try {
            const response = await axios.post(
                `${process.env.VUE_APP_API_URL}/currency/add`,
                form,
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + auth._token,
                    },
                }
            );

            const res = response.data;

            if (res.status) {
                emit("addOrUpdate");
                toast.success(res.message);
                updateModal(false);
                resetForm();
            }
        } finally {
            loader.hide();
        }
    }
};

const update = async () => {
    v$.value.$touch();
    if (!v$.value.$invalid) {
        const loader = $loading.show({
            color: "#489464",
            loader: "spinner",
        });
        try {
            const response = await axios.put(
                `${process.env.VUE_APP_API_URL}/currency/edit/${props.selectedData.uuid}`,
                form,
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + auth._token,
                    },
                }
            );

            const res = response.data;

            if (res.status) {
                emit("addOrUpdate");
                toast.success(res.message);
                updateModal(false);
                resetForm();
            }
        } finally {
            loader.hide();
        }
    }
};
</script>
